export const DEFAULT_ASSESSMENT_FILTERS = Object.freeze({
  side: "ALL",
  timeframe: "ALL",
  range: "all",
  minConfidence: "",
  minScore: "",
});

export const ASSESSMENT_TIMEFRAMES = Object.freeze(["15M", "4H", "1D"]);

export const ASSESSMENT_STAR_PREFIX = "assessment:";

export function assessmentStarKey(id) {
  return id ? `${ASSESSMENT_STAR_PREFIX}${id}` : null;
}

export function isAssessmentStar(value) {
  return value?.starredKind === "AI_ASSESSMENT" && Boolean(value?.assessmentId);
}

function threshold(value) {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 100 ? number : null;
}

export function buildAssessmentQuery(filters = DEFAULT_ASSESSMENT_FILTERS) {
  const query = {
    side: ["LONG", "SHORT"].includes(filters.side) ? filters.side : "ALL",
    timeframe: ASSESSMENT_TIMEFRAMES.includes(filters.timeframe) ? filters.timeframe : "ALL",
    range: ["1h", "4h", "24h", "7d", "30d"].includes(filters.range) ? filters.range : "all",
  };
  const minConfidence = threshold(filters.minConfidence);
  const minScore = threshold(filters.minScore);
  if (minConfidence !== null) query.minConfidence = minConfidence;
  if (minScore !== null) query.minScore = minScore;
  return query;
}

export function validAssessmentFilters(filters) {
  return [filters.minConfidence, filters.minScore].every((value) => value === ""
    || (Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 100));
}

export function appendAssessmentPage(current = [], next = []) {
  const seen = new Set();
  return [...current, ...next].filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function coverageText(coverage) {
  if (!coverage) return "Available assessment history";
  const days = Number.isFinite(Number(coverage.retentionDays))
    ? `up to ${Number(coverage.retentionDays)} days` : "retained history";
  if (coverage.truncated || coverage.partial) return `Partial assessment history · ${days}`;
  return `All available assessments · ${days}`;
}

export function coinDataFor(insights, symbol) {
  const currencies = insights?.currencyInfo || {};
  const raw = String(symbol || "").toUpperCase();
  const base = raw.replace(/(?:USDT|USD|BTC|ETH)$/, "");
  return currencies[raw] || currencies[base] || null;
}

const clone = (value) => value == null ? value : JSON.parse(JSON.stringify(value));
const epochSeconds = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.floor(numeric < 10_000_000_000 ? numeric : numeric / 1_000);
};

function chartMarket(detail, snapshot) {
  if (String(snapshot?.assetType || "").toUpperCase() === "STOCK") return null;
  const symbol = String(detail?.symbol || "").toUpperCase();
  const suffix = ["USDT", "USDC", "USD", "BTC", "ETH"].find((candidate) =>
    symbol.length > candidate.length && symbol.endsWith(candidate));
  if (!suffix) return null;
  const currency = symbol.slice(0, -suffix.length);
  if (!currency || (snapshot?.cur && ![currency, symbol].includes(String(snapshot.cur).toUpperCase()))) return null;
  return { currency, base: suffix, symbol };
}

export function assessmentSelection(detail) {
  if (!detail?.id) return null;
  const snapshot = clone(detail.eventSnapshot) || {};
  const normalizedEvent = clone(detail.event) || {};
  const market = chartMarket(detail, snapshot);
  const focusTime = epochSeconds(snapshot.ts ?? normalizedEvent.occurredAt ?? detail.assessedAt);
  const direction = String(normalizedEvent.direction || detail.side || "").toUpperCase();
  const savedTimeframe = String(snapshot.tf || normalizedEvent.timeframe || "").replace(/^_/, "").toUpperCase();
  const evaluation = {
    ...(clone(detail.result) || {}),
    evaluatedAt: detail.result?.evaluatedAt || detail.assessedAt,
    attemptId: detail.result?.attemptId || detail.id,
    symbol: detail.result?.symbol || detail.symbol,
    _sentimentContext: clone(detail.sentimentContext),
  };
  const event = {
    cur: String(snapshot.cur || market?.currency || detail.symbol || "").toUpperCase(),
    base: market?.base || null,
    marketSymbol: market?.symbol || null,
    assetType: snapshot.assetType || null,
    tf: savedTimeframe ? `_${savedTimeframe}` : null,
    name: snapshot.name || normalizedEvent.name || "Recorded assessment",
    desc: snapshot.desc || "",
    ts: focusTime,
    bull: snapshot.bull === true || direction === "LONG",
    bear: snapshot.bear === true || direction === "SHORT",
    price: Number.isFinite(Number(normalizedEvent.price)) ? Number(normalizedEvent.price)
      : Number.isFinite(Number(detail.assessedPrice)) ? Number(detail.assessedPrice) : null,
    draw: snapshot.draw || null,
    eventId: normalizedEvent.id || null,
    eventRevision: normalizedEvent.revision || null,
    codexEvaluation: evaluation,
    codexEvaluations: [],
  };
  return {
    assessmentId: detail.id,
    index: 0,
    sectionEvents: [event],
    chartMarket: market,
    chartAvailable: Boolean(market && savedTimeframe && focusTime),
    focusTime,
    contextAvailability: detail.contextAvailability || "UNAVAILABLE",
  };
}

export function assessmentStarredSnapshot(detail, selection) {
  const event = selection?.sectionEvents?.[0];
  if (!detail?.id || !event) return null;
  return {
    starredKind: "AI_ASSESSMENT",
    assessmentId: detail.id,
    id: detail.id,
    cur: event.cur,
    base: event.base,
    marketSymbol: event.marketSymbol,
    assetType: event.assetType,
    name: event.name,
    tf: event.tf,
    bull: event.bull,
    bear: event.bear,
    ts: event.ts,
    price: event.price,
    desc: event.desc,
    draw: clone(event.draw),
    codexEvaluation: clone(event.codexEvaluation),
    assessmentProvider: detail.provider,
    assessmentOutcome: detail.outcome,
    assessmentConfidence: detail.confidence,
    assessedAt: detail.assessedAt,
  };
}
